import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import { Button, ModalHeader, ModalBody, ModalFooter, Modal, Popover, PopoverHeader, PopoverBody, UncontrolledTooltip } from 'reactstrap';
// eslint-disable-next-line no-unused-vars
import StarRatingComponent from 'react-star-rating-component';
import { AudioPreview } from '../../components/FindAudio/AudioPreview';
import ModalConsumer from '../../components/utility/Modal/ModalConsumer';
import LicenseStatusSelector from '../../components/FindAudio/LicenseStatusSelector'; //license status import
import ShareForReviewModal from '../../components/FindAudio/ShareForReviewModal'; //approval review import
import { canShareForReview } from '../../utils/reviewShareGate';
import ReviewHistoryModal from '../../components/FindAudio/ReviewHistoryModal'; //review history import
import ApproveTrackModal from '../../components/FindAudio/ApproveTrackModal'; //client approve track import
import QCHistoryModal from '../../components/FindAudio/QCHistoryModal'; //qc history import
import QCFailDeleteWarningModal from '../../components/FindAudio/QCFailDeleteWarningModal';
import VersionBadge from '../../components/VersionBadge/VersionBadge';
import RequestLicenseModal from '../../components/FindAudio/RequestLicenseModal';
import RequestAccessModal from '../../components/FindAudio/RequestAccessModal';
import DownloadManagerModal from '../../components/DownloadManagerModal/DownloadManagerModal';
import { downloadFile } from '../../helpers/download';
import { usePlaylists } from '../../components/PlaylistContext';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { getOrgCollectionPath, AUDIOMETAPATH } from '../../constants/paths';
import { useOrganizationStore } from '../../store/organization.store';
import { usePublicTracksStore } from '../../store/publicTracks.store';
import { useTracksStore } from '../../store/tracks.store';
import { useToast } from '../../contexts/ToastContext';

// Helper to extract a grouping key from a track based on a field name
// Friendly labels for approval_status grouping
const APPROVAL_STATUS_LABELS = {
  unshared: 'Unshared',
  pending: 'Pending Review',
  approved: 'Approved',
  rejected: 'Rejected',
  recalled: 'Recalled',
};

const getGroupKey = (track, field) => {
  if (!field) return null;
  const val = track[field];
  if (val == null) return 'Uncategorized';
  // Friendly labels for approval_status
  if (field === 'approval_status') {
    return APPROVAL_STATUS_LABELS[val] || val || 'Uncategorized';
  }
  // Friendly labels for qc_status
  if (field === 'qc_status') {
    const QC_LABELS: Record<string, string> = { 'n/a': 'Untested', untested: 'Untested', testing: 'Testing', pass: 'Pass', fail: 'Fail' };
    return QC_LABELS[val] || val || 'Untested';
  }
  if (typeof val === 'string') return val || 'Uncategorized';
  if (Array.isArray(val)) return val.map(v => v.label || v).join(', ') || 'Uncategorized';
  if (typeof val === 'object' && val.label) return val.label;
  return String(val) || 'Uncategorized';
};

export { getGroupKey };

// Add AudioErrorBoundary component
class AudioErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.warn('Audio loading error:', error);
    }
  }

  render() {
    if (this.state.hasError) {
      // Return a simple play button as fallback
      return (
        <button 
          className="btn btn-link p-0"
          onClick={() => this.setState({ hasError: false })}
          title="Retry loading audio"
        >
          <i className="fas fa-play"></i>
        </button>
      );
    }

    return this.props.children;
  }
}

const ModalConfirm = (props) => {
  const [modal, setModal] = useState(props.isOpen);
  const toggle = () => {
    setModal(!modal);
  };
  const remove = () => {
    props.onRequestClose();
  };
  const callback = () => {
    props.callback();
    toggle();
  };
  return (
    <div>
      <Modal isOpen={modal} toggle={toggle} onClosed={remove} contentClassName="delete-confirm-modal">
        <ModalHeader 
          close={
            <button
              className="close"
              onClick={toggle}
              style={{
                color: 'var(--accent-1-color)',
                opacity: 1,
                textShadow: 'none',
                background: 'none',
                border: 'none',
                fontSize: '1.75rem',
                fontWeight: 'normal',
                padding: '0.25rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: 'auto'
              }}
            >
              ×
            </button>
          }
        >
          {props.title}
        </ModalHeader>
        <ModalBody>{props.body}</ModalBody>
        <ModalFooter>
          <Button 
            className="ml-auto btn-warning-custom" 
            onClick={callback}
            style={{ position: 'relative', zIndex: 2 }}
          >
            <span style={{ position: 'relative', zIndex: 3 }}>Continue</span>
          </Button>
          <Button 
            className="ml-2 btn-primary-custom" 
            onClick={toggle}
            style={{ position: 'relative', zIndex: 2 }}
          >
            <span style={{ position: 'relative', zIndex: 3 }}>Cancel</span>
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

// PublicToggle component for toggling track public/private status.
// `readOnly` disables interaction while still displaying the current
// state — used by studios / broadcasters / mediahouses so they can SEE
// whether a track is public without being able to change it.
//
// State is derived from membership in /publicCatalogue (via
// usePublicTracksStore) — a single source of truth that every org
// subscribes to at app boot. This decouples the toggle from per-org
// `isPublic` fields that can drift when the cross-org campaign-copy
// cascade misses a mirror doc. A track is "public" iff a doc with its
// id exists in /publicCatalogue.
const PublicToggle = ({ track, firebase, readOnly = false }) => {
  const [isLoading, setIsLoading] = useState(false);
  const currentOrgId = useOrganizationStore((state) => state.currentOrgId);
  const currentOrgName = useOrganizationStore((state) => state.currentOrgName);

  // Membership check against the global publicCatalogue subscription.
  // Zustand selector-narrowed so re-renders only fire when this
  // specific track's membership changes.
  const isPublic = usePublicTracksStore((state) => !!state.tracks[track.id]);

  // Check if this is a licensed/shared track (not owned by this org)
  const isLicensedTrack = track.isLicensedTrack === true;

  const handleToggle = async () => {
    // Don't allow toggling for licensed tracks or read-only mode
    if (isLoading || !firebase || !currentOrgId || isLicensedTrack || readOnly) return;

    setIsLoading(true);
    const newValue = !isPublic;

    try {
      // Resolve the audio-OWNER org (studio/artist that uploaded the
      // audio). Campaign copies carry `sourceOrgId` pointing at that
      // owner; source docs don't, in which case the current org IS the
      // owner. Used to stamp the public-catalogue entry with the correct
      // source-of-audio reference.
      const audioOwnerOrgId = track.sourceOrgId || currentOrgId;
      if (newValue) {
        // Publishing to public catalogue ONLY — master catalogue stays as
        // the internal cross-org linking index and is written by the
        // auto-match / QC / review pipelines, not by this toggle.
        await firebase.addToPublicCatalogue(audioOwnerOrgId, currentOrgId, currentOrgName, track.id, track);
      } else {
        // Delete the public catalogue entry.
        await firebase.removeFromPublicCatalogue(audioOwnerOrgId, track.id);
      }
      // No local state to sync — the publicCatalogue subscription fires
      // the snapshot on every org, so every PublicToggle instance
      // re-renders with the new membership state automatically.
    } catch (error) {
      console.error('Error toggling public status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Determine button state and styling. Read-only mode dims the toggle
  // (opacity 0.6), changes the cursor to `default`, and swaps the title
  // so hovering explains why it can't be flipped — without hiding the
  // state itself.
  const isDisabled = isLoading || isLicensedTrack || readOnly;
  const getCursor = () => {
    if (isLicensedTrack) return 'not-allowed';
    if (readOnly) return 'default';
    if (isLoading) return 'wait';
    return 'pointer';
  };
  const getTitle = () => {
    if (isLicensedTrack) return 'Licensed tracks cannot be made public';
    if (readOnly) return isPublic ? 'Track is public (view only)' : 'Track is private (view only)';
    return isPublic ? 'Click to make private' : 'Click to make public';
  };
  // Visual dim factor. Licensed tracks + read-only both fade slightly
  // so the toggle reads as "informational" rather than "actionable".
  const opacity = isLicensedTrack ? 0.5 : readOnly ? 0.6 : 1;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <button
        type="button"
        onClick={handleToggle}
        disabled={isDisabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '36px',
          height: '20px',
          borderRadius: '10px',
          border: 'none',
          backgroundColor: isLicensedTrack
            ? 'rgba(255, 255, 255, 0.1)'
            : (isPublic ? 'var(--accent-1-color)' : 'rgba(255, 255, 255, 0.2)'),
          cursor: getCursor(),
          transition: 'background-color 0.2s',
          position: 'relative',
          padding: 0,
          opacity,
        }}
        title={getTitle()}
      >
        <div
          style={{
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: isLicensedTrack ? 'rgba(255, 255, 255, 0.5)' : '#fff',
            position: 'absolute',
            left: isPublic ? '18px' : '2px',
            transition: 'left 0.2s',
            boxShadow: isLicensedTrack ? 'none' : '0 1px 3px rgba(0,0,0,0.3)',
          }}
        />
        {isLoading && (
          <span
            className="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
            style={{
              position: 'absolute',
              width: '12px',
              height: '12px',
              color: isPublic ? '#fff' : 'var(--accent-1-color)',
            }}
          />
        )}
      </button>
    </div>
  );
};

const TableRows = ({
  tracks,
  columnsVisible,
  editTrack,
  deleteTrack,
  activeTrack,
  setActiveTrack,
  updateTrackLicense, //license status prop
  activeRowRef,
  selectedPlaylist,
  setNextTrackToAutoplayRef,
  firebase,
  mode, // 'library' (default) or 'public'
  subMode, // 'publicLibrary' or 'masterLibrary' (only meaningful when mode==='public')
  grouped, // true when rendered inside an expanded group
}) => {
  const isPublicMode = mode === 'public';
  // Master library is a public-mode view but semantically different from
  // the outward-facing public library: master orgs use it to curate what
  // reaches the public catalogue. Used to gate the isPublic column so
  // master orgs can toggle from here.
  const isMasterLibraryView = isPublicMode && subMode === 'masterLibrary';
  // Get org-level role from organization store
  const currentOrgRole = useOrganizationStore((state) => state.currentOrgRole);
  const currentOrgId = useOrganizationStore((state) => state.currentOrgId);
  const currentOrgName = useOrganizationStore((state) => state.currentOrgName);
  const isSuperAdmin = useOrganizationStore((state) => state.isSuperAdmin);
  const isMasterOrg = useOrganizationStore((state) => state.isMasterOrg);
  // Category — used to gate the "Make Public" toggle so only agency +
  // master orgs can push tracks into the public library.
  const currentOrgCategory = useOrganizationStore((state) => state.currentOrgCategory);
  const canManagePublic = isMasterOrg || currentOrgCategory === 'agency';
  // Org-level rule: when off (default), QC must pass before review can be shared.
  const bypassQcBeforeReview = useOrganizationStore((state) => state.currentOrgBypassQcBeforeReview);
  const privateTracks = useTracksStore((state) => state.tracks);
  const { showSuccess, showWarning } = useToast();
  const role = isSuperAdmin ? 'ADMIN' : (currentOrgRole || 'USER');
  const [popoverOpen, setPopoverOpen] = useState({});
  const [selectedTracks, setSelectedTracks] = useState({});
  const [visibleTracks, setVisibleTracks] = useState(new Set());
  // State for Request License modal (public mode)
  const [requestLicenseModalOpen, setRequestLicenseModalOpen] = useState(false);
  const [selectedTrackForLicense, setSelectedTrackForLicense] = useState(null);
  // State for Share for Review modal (approval workflow)
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedTrackForReview, setSelectedTrackForReview] = useState(null);
  // State for Review History modal
  const [reviewHistoryOpen, setReviewHistoryOpen] = useState(false);
  const [selectedTrackForHistory, setSelectedTrackForHistory] = useState(null);
  // State for Approve Track modal (client view)
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [selectedTrackForApproval, setSelectedTrackForApproval] = useState(null);
  // State for QC History modal
  const [qcHistoryOpen, setQcHistoryOpen] = useState(false);
  const [selectedTrackForQC, setSelectedTrackForQC] = useState(null);
  // State for the QC-fail delete-warning modal (branch of the delete
  // flow when the track's most recent QC is 'fail' — steers users to
  // the Update workflow rather than a destructive delete that would
  // break all campaign links).
  const [qcFailDeleteTrack, setQcFailDeleteTrack] = useState(null);
  // State for Download Manager modal (single file)
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadModalFile, setDownloadModalFile] = useState(null);
  // State for Request Access modal (public library, restricted tracks)
  const [requestAccessOpen, setRequestAccessOpen] = useState(false);
  const [selectedTrackForAccess, setSelectedTrackForAccess] = useState(null);
  // Set of trackIds with pending access requests from this org
  const [pendingAccessRequestTrackIds, setPendingAccessRequestTrackIds] = useState(new Set());
  const ROW_BUFFER_COUNT = 10; // Number of rows to buffer above and below
  const [visibleIndices, setVisibleIndices] = useState(new Set());
  const [rowHeight, setRowHeight] = useState(0);
  const wavePlayerRefs = useRef(new Map());
  const { focusedPlaylists, addTrackToPlaylist, playlistTrackIds, removeTrackFromPlaylist } = usePlaylists();
  const [addingToPlaylists, setAddingToPlaylists] = useState({}); // { [trackId]: true/false }
  // State to store audio durations for visible tracks
  const [audioDurations, setAudioDurations] = useState({});
  // State for tag adder UI
  const [tagAdderOpen, setTagAdderOpen] = useState({}); // { [trackId]: boolean }
  const [tagAdderValue, setTagAdderValue] = useState({}); // { [trackId]: tagObj|null }
  const [availableTags, setAvailableTags] = useState([]);
  // State for tag hover and removal
  const [hoveredTag, setHoveredTag] = useState({}); // { [trackId]: tagValue }
  // const [removingTag, setRemovingTag] = useState({}); // { [trackId]: tagValue }
  // const [pendingRemove, setPendingRemove] = useState(null); // { trackId, tagValue, tagLabel }

  // --- New refs for popover and button ---
  const popoverRefs = useRef({});
  const buttonRefs = useRef({});

  // --- Helper to check if node is a DOM node ---
  const isDOMNode = node => node && typeof node.contains === 'function';

  // Subscribe to my pending access requests (to show "Pending" state on Request Access button)
  useEffect(() => {
    if (!isPublicMode || !firebase || !currentOrgId || !firebase.subscribeMyAccessRequests) {
      setPendingAccessRequestTrackIds(new Set());
      return;
    }
    const unsub = firebase.subscribeMyAccessRequests(currentOrgId, (requests) => {
      setPendingAccessRequestTrackIds(new Set(requests.map((r) => r.trackId)));
    });
    return () => { if (unsub) unsub(); };
  }, [isPublicMode, firebase, currentOrgId]);

  // --- Effect to close popover on outside click ---
  useEffect(() => {
    const handleClick = (event) => {
      let shouldClose = false;
      Object.keys(popoverOpen).forEach((id) => {
        if (popoverOpen[id]) {
          const popoverNode = popoverRefs.current[id];
          const buttonNode = buttonRefs.current[id];
          if (
            (!isDOMNode(popoverNode) || !popoverNode.contains(event.target)) &&
            (!isDOMNode(buttonNode) || !buttonNode.contains(event.target))
          ) {
            shouldClose = true;
          }
        }
      });
      if (shouldClose) {
        setPopoverOpen((prev) => {
          const newState = { ...prev };
          Object.keys(newState).forEach((id) => {
            newState[id] = false;
          });
          return newState;
        });
      }
    };
    // Only add listener if any popover is open
    if (Object.values(popoverOpen).some(Boolean)) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [popoverOpen]);

  // Cleanup function for wave players
  const cleanupWavePlayer = (trackId) => {
    const wavePlayer = wavePlayerRefs.current.get(trackId);
    if (wavePlayer) {
      // Stop any playing audio
      if (wavePlayer.isPlaying()) {
        wavePlayer.stop();
      }
      // Destroy the wave player instance
      wavePlayer.destroy();
      // Remove from refs
      wavePlayerRefs.current.delete(trackId);
    }
  };

  // Calculate row height when component mounts
  useEffect(() => {
    const calculateRowHeight = () => {
      const firstRow = document.querySelector('[data-track-id]');
      if (firstRow) {
        const height = firstRow.getBoundingClientRect().height;
        setRowHeight(height);
      }
    };

    calculateRowHeight();
    // Recalculate on window resize
    window.addEventListener('resize', calculateRowHeight);
    return () => window.removeEventListener('resize', calculateRowHeight);
  }, []);

  // Add intersection observer to track visible rows
  useEffect(() => {
    if (!rowHeight) return; // Don't set up observer until we have row height

    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleIndices(prev => {
          const newSet = new Set(prev);
          let changed = false;
          entries.forEach((entry) => {
            const trackId = entry.target.dataset.trackId;
            const index = tracks.findIndex(t => t.id === trackId);
            if (index === -1) return;
            if (entry.isIntersecting) {
              if (!newSet.has(index)) {
                newSet.add(index);
                changed = true;
              }
            } else {
              if (newSet.has(index)) {
                newSet.delete(index);
                changed = true;
                // Cleanup wave player when track is no longer visible
                cleanupWavePlayer(trackId);
              }
            }
          });
          return changed ? newSet : prev;
        });
      },
      { 
        threshold: 0.1,
        rootMargin: `${ROW_BUFFER_COUNT * rowHeight}px 0px` // Buffer based on row count
      }
    );

    // Observe all track rows
    const trackRows = document.querySelectorAll('[data-track-id]');
    trackRows.forEach(row => observer.observe(row));

    // Capture current refs for cleanup
    const currentRefs = wavePlayerRefs.current;

    return () => {
      trackRows.forEach(row => observer.unobserve(row));
      observer.disconnect();
      // Cleanup all wave players on unmount using captured refs
      currentRefs.forEach((_, trackId) => {
        cleanupWavePlayer(trackId);
      });
    };
  }, [tracks, rowHeight]);

  // Compute the visibleTracks set based on visibleIndices
  useEffect(() => {
    if (visibleIndices.size === 0) {
      setVisibleTracks(new Set());
      return;
    }
    const indices = Array.from(visibleIndices);
    const min = Math.max(0, Math.min(...indices) - ROW_BUFFER_COUNT);
    const max = Math.min(tracks.length - 1, Math.max(...indices) + ROW_BUFFER_COUNT);
    
    setVisibleTracks(prevVisibleTracks => {
      const newVisibleTracks = new Set();
      
      // Cleanup wave players for tracks that are no longer in the buffer
      tracks.forEach(track => {
        if (!newVisibleTracks.has(track.id) && prevVisibleTracks.has(track.id)) {
          cleanupWavePlayer(track.id);
        }
      });

      for (let i = min; i <= max; i++) {
        newVisibleTracks.add(tracks[i].id);
      }
      
      if (activeTrack) {
        const activeTrackIndex = tracks.findIndex(t => t.id === activeTrack);
        if (activeTrackIndex !== -1) {
          const activeMin = Math.max(0, activeTrackIndex - ROW_BUFFER_COUNT);
          const activeMax = Math.min(tracks.length - 1, activeTrackIndex + ROW_BUFFER_COUNT);
          for (let i = activeMin; i <= activeMax; i++) {
            newVisibleTracks.add(tracks[i].id);
          }
        }
      }
      return newVisibleTracks;
    });
  }, [visibleIndices, tracks, activeTrack]);

  // Cleanup on component unmount
  useEffect(() => {
    // Capture current refs for cleanup
    const currentRefs = wavePlayerRefs.current;
    
    return () => {
      // Cleanup all wave players when component unmounts using captured refs
      currentRefs.forEach((_, trackId) => {
        cleanupWavePlayer(trackId);
      });
      currentRefs.clear();
    };
  }, []);

  const togglePopover = (id) => {
    setPopoverOpen(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleTrackChange = (trackId, track) => {
    setSelectedTracks(prev => ({
      ...prev,
      [trackId]: track
    }));
    const activeTrackId = track.storageRef.split('/')[0];
    setActiveTrack(activeTrackId);
    togglePopover(trackId);
    // Clear previous duration so AudioPreview will recalculate for new file
    setAudioDurations(prev => {
      const newDurations = { ...prev };
      delete newDurations[trackId];
      return newDurations;
    });
  };

  const handleDownload = async (event, track) => {
    event.preventDefault();
    if (track.downloadUrl) {
      downloadFile({
        fileName: track.fileName || track.storageRef || 'download',
        fileType: 'audio/mpeg', // or 'audio/wav' depending on your file type
        fileUrl: track.downloadUrl,
      });
    } else {
      console.error('Download URL is not available');
    }
  };

  // Request License handlers (for public mode)
  const handleRequestLicense = (track) => {
    setSelectedTrackForLicense(track);
    setRequestLicenseModalOpen(true);
  };

  const handleCloseRequestModal = () => {
    setRequestLicenseModalOpen(false);
    setSelectedTrackForLicense(null);
  };

  const handleSubmitLicenseRequest = async (requestData) => {
    if (!selectedTrackForLicense || !firebase || !currentOrgId) return;

    try {
      await firebase.createLicenseRequest(
        selectedTrackForLicense.sourceOrgId,
        selectedTrackForLicense.sourceTrackId || selectedTrackForLicense.id,
        selectedTrackForLicense.trackName || 'Unknown Track',
        currentOrgId,
        currentOrgName,
        {
          ...requestData,
          trackData: {
            genre: selectedTrackForLicense.genre,
            bpm: selectedTrackForLicense.bpm,
            key: selectedTrackForLicense.key,
            artist: selectedTrackForLicense.artist,
            album: selectedTrackForLicense.album,
          },
        }
      );
      handleCloseRequestModal();
    } catch (error) {
      console.error('Error submitting license request:', error);
      throw error;
    }
  };

  // Use defaultMp3Id for preview if present, else fallback to first mp3 (current logic)
  const getPreviewMp3 = (mp3Files, defaultMp3Id) => {
    // Always prefer the latest version if versioned files exist
    if (mp3Files.length > 1 && mp3Files.some(f => f.version)) {
      const sorted = [...mp3Files].sort((a, b) => (b.version || 0) - (a.version || 0));
      return sorted[0];
    }
    // Fall back to defaultMp3Id
    if (defaultMp3Id) {
      const found = mp3Files.find(f => f.storageRef === defaultMp3Id);
      if (found) return found;
    }
    return mp3Files[0];
  };

  // Load tags from Firestore once
  useEffect(() => {
    if (!firebase?.db) return;
    const loadTags = async () => {
      const tagsRef = collection(firebase.db, 'tags');
      const tagsSnapshot = await getDocs(tagsRef);
      const tagsData = [];
      tagsSnapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        tagsData.push({
          id: docSnap.id,
          name: data.label,
          value: data.value,
          label: data.label
        });
      });
      tagsData.sort((a, b) => a.name.localeCompare(b.name));
      setAvailableTags(tagsData);
    };
    loadTags();
  }, [firebase]);

  return [
    ...tracks.map((track) => {
      const {
        // eslint-disable-next-line no-unused-vars
        trackColor,
        trackName,
        composer = [],
        tags = [],
        genre,
        bpm,
        key,
        id,
        // eslint-disable-next-line no-unused-vars
        trackRating,
        jobTitle,
        producer,
        artist,
        album,
        recordLabel,
        publisher,
        agency,
        client,
        creativedirector = [],
        dateCompleted,
        dateExpires,
        flightingPlatform = [],
        flightingCodes = [],
        studio,
        backupLocation,
        sessionArtists = [],
        mp3Files = [],
        wavFiles = [],
        isLicensedTrack,
        sourceOrgName,
        // sourceOrgId available for future use (e.g., audio playback from source org)
      } = track;

      // console.log('Wav', wavFiles);
      // console.log('Mp3', mp3Files);

      // For public library view: track is restricted if it's not in the current org's private library
      // (and not the org's own track — master org sees their own tracks as accessible)
      const isRestricted = isPublicMode && !isMasterOrg && !privateTracks[id] && track.sourceOrgId !== currentOrgId;

      const rowStyle = {
        ...(grouped ? { backgroundColor: 'rgba(255, 255, 255, 0.03)' } : {}),
        ...(activeTrack === track.id ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}),
        ...(isRestricted ? { opacity: 0.5 } : {}),
      };

      const tagsList = () => {
        if (tags && tags.length) {
          return (
            <div className="tableTag-container" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0, padding: '2px 5px' }}>
              {tags.map((item) => (
                <ModalConsumer key={uuidv4()}>
                  {({ showModal }) => (
                    <span
                      className="tableTag"
                      style={{
                        display: 'inline-block',
                        margin: 0,
                        padding: '0 5px',
                        height: 15,
                        lineHeight: '15px',
                        position: 'relative',
                        cursor: (['ADMIN', 'OPERATOR'].includes(role) && hoveredTag[id] === item.value) ? 'pointer' : 'default',
                        textDecoration: (['ADMIN', 'OPERATOR'].includes(role) && hoveredTag[id] === item.value) ? 'line-through' : 'none',
                        textDecorationColor: (['ADMIN', 'OPERATOR'].includes(role) && hoveredTag[id] === item.value) ? 'var(--accent-1-color)' : 'inherit',
                        color: (['ADMIN', 'OPERATOR'].includes(role) && hoveredTag[id] === item.value) ? 'var(--accent-1-color)' : undefined,
                        transition: 'color 0.15s',
                      }}
                      onMouseEnter={() => ['ADMIN', 'OPERATOR'].includes(role) && setHoveredTag(prev => ({ ...prev, [id]: item.value }))}
                      onMouseLeave={() => ['ADMIN', 'OPERATOR'].includes(role) && setHoveredTag(prev => ({ ...prev, [id]: null }))}
                      title={(['ADMIN', 'OPERATOR'].includes(role) && hoveredTag[id] === item.value) ? 'Remove tag' : undefined}
                      onClick={e => {
                        if (['ADMIN', 'OPERATOR'].includes(role) && hoveredTag[id] === item.value) {
                          e.stopPropagation();
                          showModal(ModalConfirm, {
                            isOpen: true,
                            title: 'Remove Tag',
                            body: `Are you sure you want to remove the tag "${item.label}"?`,
                            callback: async () => {
                              const trackRef = doc(firebase.db, getOrgCollectionPath(currentOrgId, AUDIOMETAPATH), id);
                              const newTags = tags.filter(t => t.value !== item.value);
                              await updateDoc(trackRef, { tags: newTags });
                              // Sync tags to master catalogue
                              try { await firebase.syncMasterCatalogue(currentOrgId, id, { tags: newTags }); } catch { /* best effort */ }
                              setHoveredTag(prev => ({ ...prev, [id]: null }));
                            },
                            onRequestClose: () => setHoveredTag(prev => ({ ...prev, [id]: null })),
                          });
                        }
                      }}
                    >
                      {item.label}
                    </span>
                  )}
                </ModalConsumer>
              ))}
              {/* Add tag adder button if tags column is visible and user is Admin/Operator */}
              {columnsVisible.tags && ['ADMIN', 'OPERATOR'].includes(role) && (
                <>
                  <button
                    className="btn btn-link p-0 ml-1"
                    style={{ fontSize: '0.85rem', color: '#6b6bff', lineHeight: 1, padding: 0, width: 18, height: 15, minWidth: 0 }}
                    title="Add tag"
                    onClick={e => {
                      e.stopPropagation();
                      setTagAdderOpen(prev => ({ ...prev, [id]: !prev[id] }));
                    }}
                  >
                    <i className="fas fa-plus-circle" style={{ fontSize: 13, verticalAlign: 'middle' }}></i>
                  </button>
                  {tagAdderOpen[id] && (
                    <div style={{ minWidth: 180, background: 'var(--color-background-secondary)', borderRadius: 6, padding: 8, position: 'absolute', zIndex: 1000 }}>
                      <Autocomplete
                        options={availableTags}
                        getOptionLabel={option => option.label}
                        value={tagAdderValue[id] || null}
                        onChange={async (event, newValue) => {
                          setTagAdderValue(prev => ({ ...prev, [id]: newValue }));
                          setTagAdderOpen(prev => ({ ...prev, [id]: false }));
                          if (!newValue) return;
                          // Add tag to Firestore for this track
                          const trackRef = doc(firebase.db, getOrgCollectionPath(currentOrgId, AUDIOMETAPATH), id);
                          // Merge with existing tags, avoid duplicates
                          const newTags = Array.isArray(tags)
                            ? [...tags.filter(t => t.value !== newValue.value), { label: newValue.label, value: newValue.value }]
                            : [{ label: newValue.label, value: newValue.value }];
                          await updateDoc(trackRef, { tags: newTags });
                          // Sync tags to master catalogue
                          try { await firebase.syncMasterCatalogue(currentOrgId, id, { tags: newTags }); } catch { /* best effort */ }
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Add tag"
                            size="small"
                            autoFocus
                            InputProps={{
                              ...params.InputProps,
                              style: {
                                fontSize: 'var(--menu-font-size)',
                                fontFamily: 'var(--font-family-primary)',
                                color: '#fff',
                                background: 'var(--color-background-secondary)'
                              }
                            }}
                            inputProps={{
                              ...params.inputProps,
                              style: {
                                color: '#fff',
                                fontSize: 'var(--menu-font-size)',
                                fontFamily: 'var(--font-family-primary)',
                                background: 'var(--color-background-secondary)'
                              }
                            }}
                          />
                        )}
                        style={{ width: 160, fontSize: 'var(--menu-font-size)', fontFamily: 'var(--font-family-primary)' }}
                        ListboxProps={{ style: { fontSize: 'var(--menu-font-size)', fontFamily: 'var(--font-family-primary)' } }}
                        disableClearable
                        openOnFocus
                        onBlur={() => setTagAdderOpen(prev => ({ ...prev, [id]: false }))}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          );
        }
      };
      const composersList = () => {
        if (composer && composer.length) {
          return composer.map((item) => {
            return (
              <span key={uuidv4()} className="mr-2">
                {item.label}
              </span>
            );
          });
        }
      };

      const producerList = () => {
        if (producer) {
          return (
            <span key={uuidv4()} className="mr-2">
              {producer.label}
            </span>
          );
        }
      };

      const creativeDirectorsList = () => {
        if (creativedirector && creativedirector.length) {
          return creativedirector.map((item) => {
            return (
              <span key={uuidv4()} className="mr-2">
                {item.label}
              </span>
            );
          });
        }
      };

      const flightingPlatformList = () => {
        if (flightingPlatform && flightingPlatform.length) {
          return flightingPlatform.map((item) => {
            return (
              <span key={uuidv4()} className="mr-2">
                {item.label}
              </span>
            );
          });
        }
      };

      const sessionArtistsList = () => {
        if (sessionArtists && sessionArtists.length) {
          return sessionArtists.map((item) => {
            return (
              <span key={uuidv4()} className="mr-2">
                {item.label}
              </span>
            );
          });
        }
      };

      const hasAudio = wavFiles.length > 0 || mp3Files.length > 0;
      const selectedTrack = selectedTracks[id]
        || (mp3Files.length > 0
          ? getPreviewMp3(mp3Files, track.defaultMp3Id)
          : wavFiles[0] || {});
      // Determine if the track is already in any focused playlist
      const isInAnyFocusedPlaylist = focusedPlaylists.some(pid => (playlistTrackIds[pid] || []).includes(id));

      // Move isInSelectedPlaylist logic here, where 'id' is defined
      const isInSelectedPlaylist = selectedPlaylist && (playlistTrackIds[selectedPlaylist] || []).includes(id);

      return (
        <React.Fragment key={id}>
          <tr 
            data-track-id={id}
            style={rowStyle}
            ref={track.id === activeTrack ? activeRowRef : null}
          >
            {columnsVisible.trackName && (
              <td>
                <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                  <span>{trackName}</span>
                  {isLicensedTrack && (
                    <span
                      id={`licensed-badge-${id}`}
                      style={{
                        fontSize: '0.65rem',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: 'rgba(var(--accent-1-rgb, 255, 82, 82), 0.2)',
                        color: 'var(--accent-1-color)',
                        border: '1px solid var(--accent-1-color)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Licensed
                    </span>
                  )}
                  {track.isRapidUploadTrack && (
                    <span
                      id={`rapid-upload-badge-${id}`}
                      style={{
                        fontSize: '0.65rem',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: 'rgba(255, 180, 50, 0.2)',
                        color: '#ffb432',
                        border: '1px solid rgba(255, 180, 50, 0.5)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Rapid Upload
                    </span>
                  )}
                </div>
                {isLicensedTrack && sourceOrgName && (
                  <UncontrolledTooltip target={`licensed-badge-${id}`} placement="top">
                    Licensed from: {sourceOrgName}
                  </UncontrolledTooltip>
                )}
                {track.isRapidUploadTrack && track.rapidUploadFrom && (
                  <UncontrolledTooltip target={`rapid-upload-badge-${id}`} placement="top">
                    Rapid upload from: {track.rapidUploadFrom}
                  </UncontrolledTooltip>
                )}
                {columnsVisible.audioFiles && hasAudio && visibleTracks.has(id) && (
                  <AudioErrorBoundary>
                    <AudioPreview
                      mp3Files={mp3Files}
                      wavFiles={wavFiles}
                      id={id}
                      activeTrack={activeTrack}
                      setActiveTrack={setActiveTrack}
                      showButton={false}
                      selectedTrack={selectedTrack}
                      onWavePlayerInit={(wavePlayer) => {
                        wavePlayerRefs.current.set(id, wavePlayer);
                      }}
                      onDuration={(duration) => {
                        setAudioDurations(prev => {
                          // Only update if changed or not set
                          if (prev[id] !== duration) {
                            return { ...prev, [id]: duration };
                          }
                          return prev;
                        });
                      }}
                    />
                  </AudioErrorBoundary>
                )}
                {columnsVisible.tags && tags && tags.length > 0 && (
                  <div className="d-flex flex-wrap mt-2">{tagsList()}</div>
                )}
              </td>
            )}
            <td className="text-right">
              {/* 1. Add to focused playlists button */}
              <span id={`add-playlist-btn-wrapper-${id}`}> 
                <Button
                  id={`add-playlist-btn-${id}`}
                  className="btn-icon btn-round mb-0 ml-1"
                  size="sm"
                  style={{ 
                    background: 'transparent',
                    color: isInAnyFocusedPlaylist ? 'var(--accent-1-color)' : '#ffffff',
                    opacity: !isInAnyFocusedPlaylist && focusedPlaylists.length === 0 ? 0.5 : 1,
                    transition: 'none'
                  }}
                  disabled={focusedPlaylists.length === 0 || addingToPlaylists[id] || isInAnyFocusedPlaylist}
                  onClick={async () => {
                    setAddingToPlaylists(prev => ({ ...prev, [id]: true }));
                    try {
                      await Promise.all(
                        focusedPlaylists.map(pid => addTrackToPlaylist(pid, id))
                      );
                    } finally {
                      setTimeout(() => setAddingToPlaylists(prev => ({ ...prev, [id]: false })), 800);
                    }
                  }}
                >
                  <span className="d-flex flex-row align-items-center">
                    {isInAnyFocusedPlaylist ? (
                      <i className="fas fa-check-circle"></i>
                    ) : (
                    <i className="fas fa-plus-circle"></i>
                    )}
                  </span>
                </Button>
              </span>
              <UncontrolledTooltip 
                key={
                  selectedPlaylist && isInSelectedPlaylist
                    ? 'already-in-playlist'
                    : focusedPlaylists.length === 0
                      ? 'no-playlist'
                      : 'other'
                }
                placement="top" 
                target={`add-playlist-btn-wrapper-${id}`}
                autohide={true}
                delay={{ show: 2000, hide: 0 }}
                trigger={selectedPlaylist && isInSelectedPlaylist ? 'hover' : (focusedPlaylists.length === 0 ? 'hover click' : 'hover')}
                container="body"
                fade={false}
              >
                {selectedPlaylist && isInSelectedPlaylist
                  ? 'Already in playlist'
                  : (isInAnyFocusedPlaylist
                      ? 'Already in playlist'
                      : (focusedPlaylists.length > 0
                          ? 'Add to checked playlists'
                          : 'Select checkboxes on playlists to add tracks'))}
              </UncontrolledTooltip>
              {/* 2. Remove from playlist button (if visible) */}
              {selectedPlaylist && isInSelectedPlaylist && (
                <>
                  <Button
                    id={`remove-playlist-btn-${id}`}
                    className="btn-icon btn-round mb-0 ml-1 remove-from-playlist-btn"
                    size="sm"
                    style={{
                      background: 'transparent',
                      color: '#ffffff',
                      transition: 'color 0.2s'
                    }}
                    onClick={async () => {
                      // Only trigger next-track autoplay if the removed track is the currently active track
                      if (activeTrack === id) {
                        const currentIndex = tracks.findIndex(t => t.id === id);
                        let nextTrackId = null;
                        if (currentIndex !== -1) {
                          // Prefer next track, otherwise previous if at end
                          if (currentIndex < tracks.length - 1) {
                            nextTrackId = tracks[currentIndex + 1].id;
                          } else if (currentIndex > 0) {
                            nextTrackId = tracks[currentIndex - 1].id;
                          }
                          if (nextTrackId && nextTrackId !== id && setNextTrackToAutoplayRef) {
                            setNextTrackToAutoplayRef(nextTrackId);
                          }
                        }
                      }
                      await removeTrackFromPlaylist(selectedPlaylist, id);
                    }}
                  >
                    <span className="d-flex flex-row align-items-center">
                      <i className="fas fa-minus-circle"></i>
                    </span>
                  </Button>
                  <UncontrolledTooltip placement="top" target={`remove-playlist-btn-${id}`} autohide={true} delay={{ show: 2000, hide: 0 }} trigger="hover" container="body" fade={false}>Remove from selected playlist</UncontrolledTooltip>
                </>
              )}
              {/* 3. Select audio tracks button */}
              {hasAudio && !isRestricted && (
                <>
                  <Button
                    id={`audio-btn-${id}`}
                    className="btn-icon btn-round mb-0 ml-1"
                    size="sm"
                    style={{
                      background: 'transparent',
                      color: '#ffffff',
                      transition: 'none',
                    }}
                    onClick={() => togglePopover(id)}
                    ref={el => (buttonRefs.current[id] = el)}
                  >
                    <span className="d-flex flex-row align-items-center">
                      <i className="fas fa-list"></i>
                    </span>
                  </Button>
                  <UncontrolledTooltip placement="top" target={`audio-btn-${id}`} autohide={true} delay={{ show: 2000, hide: 0 }} trigger="hover" container="body" fade={false}>Audio files & download</UncontrolledTooltip>
                  <Popover
                    placement="bottom"
                    isOpen={popoverOpen[id]}
                    target={`audio-btn-${id}`}
                    toggle={() => togglePopover(id)}
                    innerRef={el => (popoverRefs.current[id] = el)}
                    container="body"
                    fade={false}
                  >
                    <PopoverHeader>Select Audio File</PopoverHeader>
                    <PopoverBody>
                      {(() => {
                        // Compute max QC version per file version
                        const maxQcPerVer = {};
                        mp3Files.forEach(f => {
                          const v = f.version || 1;
                          const qv = f.qcVersion || 1;
                          if (!maxQcPerVer[v] || qv > maxQcPerVer[v]) maxQcPerVer[v] = qv;
                        });
                        return mp3Files
                        .slice() // copy array
                        .sort((a, b) => {
                          // Sort by version descending (latest first)
                          const vDiff = (b.version || 0) - (a.version || 0);
                          if (vDiff !== 0) return vDiff;
                          return (a.fileName || '').localeCompare(b.fileName || '');
                        })
                        .map((fileEntry, index) => {
                          // Determine per-file status using max QC per version
                          const currentFileVer = track.fileVersion || 1;
                          const isOlderFileVer = fileEntry.version && fileEntry.version < currentFileVer;
                          const maxQcForThisVer = maxQcPerVer[fileEntry.version || 1] || 1;
                          const isOlderQcWithinVer = fileEntry.qcVersion && fileEntry.qcVersion < maxQcForThisVer;
                          const fileApprovalStatus = isOlderFileVer ? 'rejected' : track.approval_status;
                          let fileQcStatus;
                          if (isOlderFileVer) {
                            // Older file version: the latest QC for that version passed (version was bumped due to rejection, not QC fail)
                            // Earlier QC runs within that version failed (superseded by QC fix)
                            fileQcStatus = isOlderQcWithinVer ? 'fail' : 'pass';
                          } else {
                            // Current file version: use track's live QC status for the latest QC, fail for older QC runs
                            fileQcStatus = isOlderQcWithinVer ? 'fail' : track.qc_status;
                          }
                          const showQcBadge = fileEntry.qcVersion >= 1 && (isOlderQcWithinVer || isOlderFileVer || fileQcStatus === 'pass' || fileQcStatus === 'fail' || fileQcStatus === 'testing');
                          return (
                          <button
                            key={`${fileEntry.storageRef}-${index}`}
                            className={`popover-action ${selectedTrack.storageRef === fileEntry.storageRef ? 'selected' : ''}`}
                            onClick={() => handleTrackChange(id, fileEntry)}
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              {(fileEntry.fileName || '').replace(/\.mp3$/i, '')}
                              {fileEntry.version && (
                                <VersionBadge type="version" version={fileEntry.version} status={fileApprovalStatus} />
                              )}
                              {showQcBadge && (
                                <VersionBadge type="qc" version={fileEntry.qcVersion} status={fileQcStatus} />
                              )}
                            </span>
                            <button
                              className="download-btn"
                              style={{
                                background: 'transparent',
                                border: 'none',
                                padding: '0',
                                marginLeft: '8px',
                                color: 'inherit'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const ext = (fileEntry.fileName || fileEntry.storageRef || '').match(/\.[^.]+$/)?.[0] || '.mp3';
                                setDownloadModalFile({
                                  id: id,
                                  originalName: fileEntry.fileName || fileEntry.storageRef || 'download',
                                  customName: (fileEntry.fileName || fileEntry.storageRef || 'download').replace(/\.[^.]+$/, ''),
                                  downloadUrl: fileEntry.downloadUrl,
                                  fileType: ext.replace('.', ''),
                                  trackName: trackName,
                                });
                                setDownloadModalOpen(true);
                                setPopoverOpen(prev => ({ ...prev, [id]: false }));
                              }}
                            >
                              <i className="fas fa-download"></i>
                            </button>
                          </button>
                          );
                        });
                      })()}
                    </PopoverBody>
                  </Popover>
                </>
              )}
              {/* 4. Edit track button - hidden in public mode */}
              {!isPublicMode && ['ADMIN', 'OPERATOR'].includes(role) && (
                <>
                  <Button
                    id={`edit-btn-${id}`}
                    className="btn-icon btn-round mb-0 ml-1"
                    size="sm"
                    style={{
                      background: 'transparent',
                      color: '#ffffff',
                      transition: 'none'
                    }}
                    onClick={() => {
                      editTrack(id);
                    }}
                  >
                    <span className="d-flex flex-row align-items-center">
                      <i className="tim-icons icon-pencil"></i>
                    </span>
                  </Button>
                  <UncontrolledTooltip placement="top" target={`edit-btn-${id}`} autohide={true} delay={{ show: 2000, hide: 0 }} trigger="hover" container="body" fade={false}>Edit track</UncontrolledTooltip>
                </>
              )}
              {/* 5. Delete track button - hidden in public mode */}
              {!isPublicMode && role === 'ADMIN' && (
                <ModalConsumer>
                  {({ showModal }) => (
                    <>
                      <Button
                        id={`delete-btn-${id}`}
                        className="btn-icon btn-round"
                        size="sm"
                        style={{
                          background: 'transparent',
                          color: '#ffffff',
                          transition: 'none'
                        }}
                        onClick={() => {
                          // If the track's latest QC failed, steer the
                          // user toward the fix-in-place workflow instead
                          // of a destructive delete that would break
                          // every campaign link referencing this track.
                          // "Delete Anyway" is still available inside
                          // that modal for cases where deletion is
                          // genuinely what the operator wants.
                          if (track.qc_status === 'fail') {
                            setQcFailDeleteTrack(track);
                            return;
                          }
                          showModal(ModalConfirm, {
                            isOpen: true,
                            title: 'Delete Track',
                            body: `Are you sure you want to delete ${trackName}?`,
                            callback: () => {
                              deleteTrack(id);
                            },
                          });
                        }}
                      >
                        <span className="d-flex flex-row align-items-center">
                          <i className="tim-icons icon-trash-simple"></i>
                        </span>
                      </Button>
                      <UncontrolledTooltip placement="top" target={`delete-btn-${id}`} autohide={true} delay={{ show: 2000, hide: 0 }} trigger="hover" container="body" fade={false}>Delete track</UncontrolledTooltip>
                    </>
                  )}
                </ModalConsumer>
              )}
            </td>
            {isPublicMode && columnsVisible.requestAccess && (
              <td style={{ textAlign: 'center' }}>
                {isRestricted ? (
                  pendingAccessRequestTrackIds.has(id) ? (
                    <span style={{
                      display: 'inline-block', padding: '2px 8px', fontSize: '0.75rem',
                      borderRadius: '4px', fontWeight: '500',
                      backgroundColor: 'rgba(255, 180, 50, 0.2)', color: '#ffb432',
                      whiteSpace: 'nowrap',
                    }} title="Access request pending owner approval">
                      Pending
                    </span>
                  ) : (
                    <Button
                      id={`request-access-btn-${id}`}
                      className="btn-primary-custom"
                      size="sm"
                      style={{ opacity: 1, fontSize: '0.7rem', padding: '2px 8px', whiteSpace: 'nowrap' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTrackForAccess(track);
                        setRequestAccessOpen(true);
                      }}
                      title={`Request access to ${track.trackName || 'this track'}`}
                    >
                      <span style={{ position: 'relative', zIndex: 2, color: '#fff' }}>Request Access</span>
                    </Button>
                  )
                ) : (
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', fontSize: '0.75rem',
                    borderRadius: '4px', fontWeight: '500',
                    backgroundColor: 'rgba(80, 200, 120, 0.2)', color: '#50c878',
                  }}>
                    Accessible
                  </span>
                )}
              </td>
            )}
            {columnsVisible.length && (
              <td style={{ minWidth: 60, maxWidth: 70, width: 70, textAlign: 'center' }}>
                {audioDurations[id] !== undefined
                  ? moment.utc(audioDurations[id] * 1000).format('mm:ss')
                  : '--'}
              </td>
            )}
            {/* Approval Status column */}
            {columnsVisible.approvalStatus && (
              <td style={{ textAlign: 'center' }}>
                {/* Read-only badges are shown for delivery copies and for
                    campaign copies EXCEPT when the copyRole is 'studio' —
                    studios keep full QC + Review controls on their campaign
                    copies (per the role matrix). Master orgs always retain
                    full controls via the existing !isMasterOrg exception. */}
                {(isPublicMode || ((track.isDeliveryTrack || (track.isCampaignCopy && track.copyRole !== 'studio')) && !isMasterOrg)) ? (
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', fontSize: '0.75rem',
                    borderRadius: '4px', fontWeight: '500',
                    backgroundColor: track.approval_status === 'approved' ? 'rgba(80, 200, 120, 0.2)'
                      : track.approval_status === 'rejected' ? 'rgba(255, 80, 80, 0.2)'
                      : track.approval_status === 'pending' ? 'rgba(255, 180, 50, 0.2)'
                      : 'rgba(255, 255, 255, 0.08)',
                    color: track.approval_status === 'approved' ? '#50c878'
                      : track.approval_status === 'rejected' ? '#ff5050'
                      : track.approval_status === 'pending' ? '#ffb432'
                      : 'rgba(255, 255, 255, 0.4)',
                  }}>
                    {track.approval_status === 'approved' ? 'Approved'
                      : track.approval_status === 'rejected' ? 'Rejected'
                      : track.approval_status === 'pending' ? 'Pending'
                      : 'N/A'}
                  </span>
                ) : (
                <>
                {/* Unshared: show Review button (like License button) */}
                {(track.approval_status === 'unshared' || !track.approval_status) && (
                  <Button
                    size="sm"
                    className="btn-primary-custom btn-ripple"
                    onClick={() => {
                      setSelectedTrackForHistory(track);
                      setReviewHistoryOpen(true);
                    }}
                    style={{ fontSize: '0.75rem', padding: '2px 8px', whiteSpace: 'nowrap' }}
                  >
                    Review
                  </Button>
                )}
                {/* Pending: both client and artist see Pending badge, client click opens approve modal */}
                {track.approval_status === 'pending' && (
                  track.isReviewTrack ? (
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      fontSize: '0.75rem',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(255, 180, 50, 0.2)',
                      color: '#ffb432',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                    title="Pending approval — click to review"
                    onClick={() => {
                      setSelectedTrackForApproval(track);
                      setApproveModalOpen(true);
                    }}
                    >
                      Pending
                    </span>
                  ) : (
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      fontSize: '0.75rem',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(255, 180, 50, 0.2)',
                      color: '#ffb432',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                    title="Approval pending — click to view details"
                    onClick={() => {
                      setSelectedTrackForHistory(track);
                      setReviewHistoryOpen(true);
                    }}
                    >
                      Pending
                    </span>
                  )
                )}
                {/* Rejected badge */}
                {track.approval_status === 'rejected' && (
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    fontSize: '0.75rem',
                    borderRadius: '4px',
                    backgroundColor: 'rgba(255, 80, 80, 0.2)',
                    color: '#ff5050',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                  title="Rejected — click to view details"
                  onClick={() => {
                    setSelectedTrackForHistory(track);
                    setReviewHistoryOpen(true);
                  }}
                  >
                    Rejected
                  </span>
                )}
                {/* Approved badge */}
                {track.approval_status === 'approved' && (
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    fontSize: '0.75rem',
                    borderRadius: '4px',
                    backgroundColor: 'rgba(80, 200, 120, 0.2)',
                    color: '#50c878',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                  title="Approved — click to view details"
                  onClick={() => {
                    setSelectedTrackForHistory(track);
                    setReviewHistoryOpen(true);
                  }}
                  >
                    Approved
                  </span>
                )}
                </>
                )}
              </td>
            )}
            {/* QC Status column */}
            {columnsVisible.qcStatus && (
              <td style={{ textAlign: 'center' }}>
                {/* Read-only badges for any received copy in a non-master org:
                    delivery copies AND campaign copies (studio/agency mirrors). */}
                {(isPublicMode || ((track.isDeliveryTrack || (track.isCampaignCopy && track.copyRole !== 'studio')) && !isMasterOrg)) ? (
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', fontSize: '0.75rem',
                    borderRadius: '4px', fontWeight: '500',
                    backgroundColor: track.qc_status === 'pass' ? 'rgba(80, 200, 120, 0.2)'
                      : track.qc_status === 'fail' ? 'rgba(255, 80, 80, 0.2)'
                      : track.qc_status === 'testing' ? 'rgba(255, 180, 50, 0.2)'
                      : 'rgba(255, 255, 255, 0.08)',
                    color: track.qc_status === 'pass' ? '#50c878'
                      : track.qc_status === 'fail' ? '#ff5050'
                      : track.qc_status === 'testing' ? '#ffb432'
                      : 'rgba(255, 255, 255, 0.4)',
                  }}>
                    {track.qc_status === 'pass' ? 'Pass'
                      : track.qc_status === 'fail' ? 'Fail'
                      : track.qc_status === 'testing' ? 'Testing'
                      : 'N/A'}
                  </span>
                ) : (
                <>
                {(!track.qc_status || track.qc_status === 'n/a' || track.qc_status === 'untested') && (
                  <Button
                    size="sm"
                    className="btn-primary-custom btn-ripple"
                    onClick={() => {
                      setSelectedTrackForQC(track);
                      setQcHistoryOpen(true);
                    }}
                    style={{ fontSize: '0.75rem', padding: '2px 8px', whiteSpace: 'nowrap' }}
                  >
                    QC
                  </Button>
                )}
                {track.qc_status === 'testing' && (
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', fontSize: '0.75rem',
                    borderRadius: '4px', backgroundColor: 'rgba(255, 180, 50, 0.2)',
                    color: '#ffb432', fontWeight: '500',
                  }}>
                    Testing
                  </span>
                )}
                {track.qc_status === 'pass' && (
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', fontSize: '0.75rem',
                    borderRadius: '4px', backgroundColor: 'rgba(80, 200, 120, 0.2)',
                    color: '#50c878', fontWeight: '500', cursor: 'pointer',
                  }}
                  onClick={() => { setSelectedTrackForQC(track); setQcHistoryOpen(true); }}
                  title="QC passed — click to view details"
                  >
                    Pass
                  </span>
                )}
                {track.qc_status === 'fail' && (
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', fontSize: '0.75rem',
                    borderRadius: '4px', backgroundColor: 'rgba(255, 80, 80, 0.2)',
                    color: '#ff5050', fontWeight: '500', cursor: 'pointer',
                  }}
                  onClick={() => { setSelectedTrackForQC(track); setQcHistoryOpen(true); }}
                  title="QC failed — click to view details"
                  >
                    Fail
                  </span>
                )}
                </>
                )}
              </td>
            )}
            {/* Job Link column */}
            {columnsVisible.jobLink && (
              <td style={{ textAlign: 'center' }}>
                {track.jobId ? (
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      fontSize: '0.75rem',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(80, 200, 120, 0.2)',
                      color: '#50c878',
                      fontWeight: '500',
                      cursor: isPublicMode ? 'default' : 'pointer',
                    }}
                    title={track.jobCampaign || 'Campaign'}
                    onClick={isPublicMode ? undefined : (e) => {
                      e.stopPropagation();
                      window.location.href = `/jobs/${track.jobId}`;
                    }}
                  >
                    {track.jobCampaign || 'Linked'}
                  </span>
                ) : track.jobCampaign ? (
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      fontSize: '0.75rem',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontWeight: '500',
                    }}
                    title={`Campaign: ${track.jobCampaign}`}
                  >
                    {track.jobCampaign}
                  </span>
                ) : (
                  <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem' }}>—</span>
                )}
              </td>
            )}
            {/* License Status - hidden in public mode */}
            {!isPublicMode && columnsVisible.licenseStatus && (
              <td style={{ textAlign: 'center' }}>
                <LicenseStatusSelector
                  track={track}
                  updateTrackLicense={updateTrackLicense}
                  firebase={firebase}
                />
              </td>
            )}
            {/* Make Public toggle — visible for every org in the
                private library so the state is transparent, but only
                agency + master can flip it. Non-eligible orgs (studios,
                broadcasters, mediahouses, etc.) see the toggle in
                read-only mode (dimmed, cursor default, tooltip explains
                it can't be edited).
                Also visible in the MASTER library for master orgs, so
                they can publish tracks from the master catalogue.
                Hidden in the public library since that's the outward-
                facing read view. */}
            {((!isPublicMode) || (isMasterLibraryView && isMasterOrg)) && columnsVisible.isPublic && (
              <td style={{ textAlign: 'center' }}>
                <PublicToggle
                  track={track}
                  firebase={firebase}
                  readOnly={!canManagePublic}
                />
              </td>
            )}
            {/* [LEGACY — DISABLED 2026-07-09] Request-License button on
                public-library rows. Kept for future revival; not part of
                the current make-public flow. */}
            {/*
            {isPublicMode && (
              <td style={{ textAlign: 'center' }}>
                <Button
                  size="sm"
                  className="btn-primary-custom"
                  onClick={() => handleRequestLicense(track)}
                  disabled={track.sourceOrgId === currentOrgId}
                  title={track.sourceOrgId === currentOrgId ? 'This is your track' : 'Request a license for this track'}
                  style={{
                    padding: '4px 12px',
                    fontSize: '12px',
                  }}
                >
                  {track.sourceOrgId === currentOrgId ? 'Your Track' : 'Request'}
                </Button>
              </td>
            )}
            */}
            {/* Language column */}
            {columnsVisible.language && (
              <td>{Array.isArray(track.language) ? track.language.map(l => l.label).join(', ') : '--'}</td>
            )}
            {/* Columns commented out for Spot Management MVP */}
            {/*
            {columnsVisible.composers && <td>{composersList()}</td>}
            {columnsVisible.genre && <td>{genre && genre.label}</td>}
            {columnsVisible.bpm && <td>{bpm && bpm.label}</td>}
            {columnsVisible.key && <td>{key && key.label}</td>}
            */}
            {columnsVisible.producer && <td>{producerList()}</td>}
            {/* Columns commented out for Spot Management MVP */}
            {/*
            {columnsVisible.artist && <td>{artist && artist.label}</td>}
            {columnsVisible.album && <td>{album && album.label}</td>}
            {columnsVisible.recordLabel && <td>{recordLabel && recordLabel.label}</td>}
            {columnsVisible.publisher && <td>{publisher && publisher.label}</td>}
            */}
            {columnsVisible.agency && <td>{agency && agency.label}</td>}
            {columnsVisible.client && <td>{client && client.label}</td>}
            {columnsVisible.creativeDirector && (
              <td>{creativeDirectorsList()}</td>
            )}
            {columnsVisible.dateCompleted && (
              <td>
                {(dateCompleted &&
                  moment(dateCompleted).format('DD-MM-YYYY')) ||
                  '--'}
              </td>
            )}
            {columnsVisible.dateExpires && (
              <td>
                {(dateExpires && moment(dateExpires).format('DD-MM-YYYY')) ||
                  '--'}
              </td>
            )}
            {/* Columns commented out for Spot Management MVP */}
            {/*
            {columnsVisible.flightingPlatform && (
              <td>{flightingPlatformList()}</td>
            )}
            {columnsVisible.flightingCode && (
              <td>{flightingCodes?.length > 0 && flightingCodes[0]}</td>
            )}
            */}
            {columnsVisible.studio && <td>{studio && studio.label}</td>}
            {columnsVisible.organization && <td>{sourceOrgName || currentOrgName || '--'}</td>}
            {/*
            {columnsVisible.backupLocation && (
              <td>{backupLocation && backupLocation.label}</td>
            )}
            {columnsVisible.sessionArtists && <td>{sessionArtistsList()}</td>}
            {columnsVisible.trackRating && <td></td>}
            */}
            {columnsVisible.flag && (
              <td style={{ textAlign: 'left', width: '60px', paddingLeft: '12px' }}>
                {['ADMIN', 'OPERATOR'].includes(role) && (
                  <>
                    <Button
                      id={`flag-btn-${id}`}
                      className="btn-icon mb-0 flag-button"
                      color="link"
                      size="sm"
                      style={{ 
                        background: 'transparent',
                        color: track.needsAttention ? '#ff4b4b' : 'rgba(255, 255, 255, 0.5)',
                        border: 'none',
                        transition: 'color 0.2s ease',
                        padding: '0.375rem 0.75rem'
                      }}
                      onClick={async () => {
                        
                        try {
                          if (!firebase || !firebase.db) {
                            console.error('Firebase or firebase.db is not available');
                            return;
                          }
                          
                          const trackRef = doc(firebase.db, 'audiometa', id);
                          const newValue = !track.needsAttention;
                          
                          await updateDoc(trackRef, { 
                            needsAttention: newValue 
                          });
                          
                        } catch (error) {
                          console.error('Error updating flag:', error);
                          console.error('Error details:', error.message, error.code);
                        }
                      }}
                    >
                      <span className="d-flex flex-row align-items-center">
                        <i className="fas fa-flag"></i>
                      </span>
                    </Button>
                    <UncontrolledTooltip placement="top" target={`flag-btn-${id}`} autohide={true} delay={{ show: 2000, hide: 0 }} trigger="hover" container="body" fade={false}>
                      Flagged tracks are not visible to basic users
                    </UncontrolledTooltip>
                  </>
                )}
              </td>
            )}
          </tr>
        </React.Fragment>
      );
    }),
    // Request License Modal (for public mode)
    isPublicMode && requestLicenseModalOpen && selectedTrackForLicense && (
      <RequestLicenseModal
        key="request-license-modal"
        isOpen={requestLicenseModalOpen}
        onClose={handleCloseRequestModal}
        onSubmit={handleSubmitLicenseRequest}
        trackName={selectedTrackForLicense.trackName || 'Unknown Track'}
        sourceOrgName={selectedTrackForLicense.sourceOrgName}
        firebase={firebase}
      />
    ),
    // QC-fail delete warning modal — shown instead of the standard
    // ModalConfirm when the operator clicks Delete on a QC-failed track.
    qcFailDeleteTrack && (
      <QCFailDeleteWarningModal
        key="qc-fail-delete-warning-modal"
        isOpen={!!qcFailDeleteTrack}
        onClose={() => setQcFailDeleteTrack(null)}
        trackName={qcFailDeleteTrack.trackName || 'this track'}
        onOpenQC={() => {
          setSelectedTrackForQC(qcFailDeleteTrack);
          setQcHistoryOpen(true);
        }}
        onDeleteAnyway={() => {
          deleteTrack(qcFailDeleteTrack.id);
        }}
      />
    ),
    // QC History Modal
    qcHistoryOpen && selectedTrackForQC && (
      <QCHistoryModal
        key="qc-history-modal"
        isOpen={qcHistoryOpen}
        onClose={() => {
          setQcHistoryOpen(false);
          setSelectedTrackForQC(null);
        }}
        trackName={selectedTrackForQC.trackName || 'Unknown Spot'}
        trackId={selectedTrackForQC.id}
        track={selectedTrackForQC}
        firebase={firebase}
        onUpdate={(!selectedTrackForQC.isDeliveryTrack && (!selectedTrackForQC.isCampaignCopy || selectedTrackForQC.copyRole === 'studio' || isMasterOrg)) ? () => {
          // `qc_status === 'fail'` gate deliberately removed — it read
          // the stale snapshot captured at modal-open time, so an
          // in-modal QC that newly failed left onUpdate undefined and
          // the Update button invisible. QCHistoryModal already gates
          // the button's visibility on its own real-time qcRecords
          // state (isLatestFailed); passing onUpdate whenever the
          // permission conditions are met is the right shape.
          setQcHistoryOpen(false);
          editTrack(selectedTrackForQC.id);
          setSelectedTrackForQC(null);
        } : undefined}
      />
    ),
    // Approve Track Modal (client view — approve/reject from table)
    approveModalOpen && selectedTrackForApproval && (
      <ApproveTrackModal
        key="approve-track-modal"
        isOpen={approveModalOpen}
        onClose={() => {
          setApproveModalOpen(false);
          setSelectedTrackForApproval(null);
        }}
        track={selectedTrackForApproval}
        firebase={firebase}
        onReviewSubmitted={() => {
          // Track status will update via Firestore listener
          setApproveModalOpen(false);
          setSelectedTrackForApproval(null);
        }}
      />
    ),
    // Review History Modal (approval details)
    reviewHistoryOpen && selectedTrackForHistory && (
      <ReviewHistoryModal
        key="review-history-modal"
        isOpen={reviewHistoryOpen}
        onClose={() => {
          setReviewHistoryOpen(false);
          setSelectedTrackForHistory(null);
        }}
        trackName={selectedTrackForHistory.trackName || 'Unknown Spot'}
        trackId={selectedTrackForHistory.isReviewTrack ? (selectedTrackForHistory.sourceTrackId || selectedTrackForHistory.id) : selectedTrackForHistory.id}
        firebase={firebase}
        sourceOrgId={(selectedTrackForHistory.isReviewTrack || selectedTrackForHistory.isDeliveryTrack || selectedTrackForHistory.isCampaignCopy) ? selectedTrackForHistory.sourceOrgId : undefined}
        onShareForReview={(selectedTrackForHistory.isReviewTrack || (selectedTrackForHistory.isCampaignCopy && selectedTrackForHistory.copyRole !== 'studio' && !isMasterOrg)) ? undefined : () => {
          // QC-before-Review gate. When the org rule is on (default) and the
          // track hasn't passed QC, refuse to open the share modal and toast
          // the reason so the user runs QC first.
          const gate = canShareForReview(selectedTrackForHistory, { bypassQcBeforeReview });
          if (!gate.ok) {
            showWarning(gate.reason || 'Cannot share for review yet.');
            return;
          }
          // Close history, open share modal for the same track (artist only)
          setReviewHistoryOpen(false);
          setSelectedTrackForReview(selectedTrackForHistory);
          setReviewModalOpen(true);
          setSelectedTrackForHistory(null);
        }}
        onRecall={(!selectedTrackForHistory.isReviewTrack && (!selectedTrackForHistory.isCampaignCopy || selectedTrackForHistory.copyRole === 'studio' || isMasterOrg) && !selectedTrackForHistory.isDeliveryTrack && selectedTrackForHistory.approval_status === 'pending') ? async () => {
          if (firebase && currentOrgId) {
            await firebase.recallReview(currentOrgId, selectedTrackForHistory.id);
            setReviewHistoryOpen(false);
            setSelectedTrackForHistory(null);
          }
        } : undefined}
        onUpdate={(!selectedTrackForHistory.isReviewTrack && (!selectedTrackForHistory.isCampaignCopy || selectedTrackForHistory.copyRole === 'studio' || isMasterOrg) && !selectedTrackForHistory.isDeliveryTrack && selectedTrackForHistory.approval_status === 'rejected') ? () => {
          setReviewHistoryOpen(false);
          editTrack(selectedTrackForHistory.id);
          setSelectedTrackForHistory(null);
        } : undefined}
      />
    ),
    // Share for Review Modal (approval workflow)
    reviewModalOpen && selectedTrackForReview && (
      <ShareForReviewModal
        key="share-for-review-modal"
        isOpen={reviewModalOpen}
        onClose={() => {
          setReviewModalOpen(false);
          setSelectedTrackForReview(null);
        }}
        onApply={async (reviewData) => {
          try {
            if (firebase && currentOrgId) {
              await firebase.createReviewRequest(
                currentOrgId,
                selectedTrackForReview.id,
                {
                  ...reviewData,
                  trackName: selectedTrackForReview.trackName || 'Unknown Spot',
                  sourceOrgName: currentOrgName || 'Unknown Organization',
                  sharedByOrgId: currentOrgId,
                  sharedByOrgName: currentOrgName || '',
                  sourceTrackOrgId: selectedTrackForReview.sourceOrgId || null,
                  // Track data snapshots for external review page
                  mp3Files: selectedTrackForReview.mp3Files || [],
                  wavFiles: selectedTrackForReview.wavFiles || [],
                  defaultMp3Id: selectedTrackForReview.defaultMp3Id || null,
                  language: selectedTrackForReview.language || [],
                  client: selectedTrackForReview.client || null,
                  agency: selectedTrackForReview.agency || null,
                  tags: selectedTrackForReview.tags || [],
                  fileVersion: selectedTrackForReview.fileVersion || 1,
                  // Spot description (audiometa.notes) — surfaces in review modals.
                  trackNotes: selectedTrackForReview.notes || '',
                }
              );
            }
          } catch (error) {
            console.error('Error creating review request:', error);
          }
          setReviewModalOpen(false);
          setSelectedTrackForReview(null);
        }}
        trackName={selectedTrackForReview.trackName || 'Unknown Spot'}
        trackId={selectedTrackForReview.id}
        firebase={firebase}
      />
    ),
    // Download Manager Modal (single file)
    downloadModalOpen && downloadModalFile && (
      <DownloadManagerModal
        key="download-manager-modal"
        isOpen={downloadModalOpen}
        onClose={() => {
          setDownloadModalOpen(false);
          setDownloadModalFile(null);
        }}
        files={[downloadModalFile]}
      />
    ),
    // Request Access Modal (public library, restricted tracks)
    requestAccessOpen && selectedTrackForAccess && (
      <RequestAccessModal
        key="request-access-modal"
        isOpen={requestAccessOpen}
        onClose={() => {
          setRequestAccessOpen(false);
          setSelectedTrackForAccess(null);
        }}
        trackName={selectedTrackForAccess.trackName || 'Unknown Spot'}
        sourceOrgName={selectedTrackForAccess.sourceOrgName}
        onSubmit={async (note) => {
          if (!firebase || !currentOrgId) return;
          const trackNameForToast = selectedTrackForAccess.trackName || 'the track';
          try {
            await firebase.createAccessRequest(
              selectedTrackForAccess.sourceOrgId,
              selectedTrackForAccess.id,
              currentOrgId,
              currentOrgName || '',
              selectedTrackForAccess.trackName || '',
              note
            );
            showSuccess(`Access request sent for ${trackNameForToast}`);
          } catch (err) {
            console.error('Error creating access request:', err);
          }
          setRequestAccessOpen(false);
          setSelectedTrackForAccess(null);
        }}
      />
    ),
  ].filter(Boolean);
};

export default TableRows;

<style>{`
.remove-from-playlist-btn:hover {
  color: var(--accent-1-color) !important;
}

button.btn.btn-link.btn-sm.btn-icon.flag-button:hover,
button.btn.btn-link.btn-sm.btn-icon.flag-button:focus,
button.btn.btn-link.btn-sm.btn-icon.flag-button:active,
.btn.btn-link.btn-sm.btn-icon.flag-button:hover,
.btn.btn-link.btn-sm.btn-icon.flag-button:focus,
.btn.btn-link.btn-sm.btn-icon.flag-button:active {
  color: #ffffff !important;
  background: transparent !important;
  background-color: transparent !important;
  border-color: transparent !important;
  box-shadow: none !important;
  transform: none !important;
  text-decoration: none !important;
}
`}</style>
